import React, { useEffect, useState } from 'react';
import { Grid, Button, FormControl, FormControlLabel, FormGroup, FormLabel, Checkbox, Select, Input, MenuItem, Typography, Box, Link } from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import HomeIcon from '@material-ui/icons/Home';
import FilterIcon from '@material-ui/icons/FilterList';
import { CommunityIcon, DivisionIcon } from 'global/components/PerformIcons';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, size, isArray, xor } from 'global/lib/lodash';
import setFilter from 'redux/actions/setFilter';
import { Filter } from 'global/utils/filter';
import useStyles from 'global/styles/filters';
import { useScreen } from 'global/styles';
import DiagContainer from 'global/components/diag-container';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { sortAlphaNum } from 'global/utils';
import { FilterChips } from 'global/components/filterchips';
import { QualityType, RealmTypes } from 'global/lib/filter';
import clsx from 'clsx';
import { DateRanges, ReportDataStatus } from '../../../global/lib/filter';
import { Text } from '../../../global/components/text';
import { Row } from '../../../global/components/row';

const ICONS = {
  HomeIcon,
  CommunityIcon,
  DivisionIcon,
};

export default function PerformReportsFilters(props) {
  const { display } = props;
  const vendors = get(props, 'vendors');
  const [showFilters, setShowFilters] = useState(false);
  const useInternalFilter = !!props.filter;
  const filter = useSelector((state) => (useInternalFilter ? props.filter : get(state, `filter.reports.${display}`, Filter())));
  const user = useSelector((state) => get(state, 'auth.user'));
  const dispatch = useDispatch();
  const [intFilter, setIntFilter] = useState(filter);
  const [state, setState] = useState({
    useExact: false,
  });
  const { realm_type, start_date, end_date, scorecard_id, superset_id, user_id, quality_type, status, edition_id, daterange } = useInternalFilter ? intFilter : filter;

  const editions = useRetrieveData(
    { url: '/editions' },
    {
      useEffect: true,
      waitFor: display === 'data',
      params: {
        filter: {
          latest: 1,
        },
      },
      build: ['editions'],
      useCache: { reload: 20, store: `data.latesteditions` },
    }
  );

  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      useCache: { reload: 20, store: 'data.scorecards' },
    }
  );
  const content_realm_id = get(user, 'realm.id');
  const users = useRetrieveData(
    { url: '/users' },
    {
      waitFor: display === 'photo' ? undefined : null,
      build: ['users'],
      params: {
        filter: content_realm_id && {
          content_realm_id,
        },
      },
      useCache: { reload: 1 },
    }
  );
  const supersets = useRetrieveData(
    { url: '/supersets' },
    {
      build: ['supersets'],
      useCache: { reload: 20 },
    }
  );

  const prefixToUse = 'vendor';

  const { isMobile } = useScreen();

  const classes = useStyles(props);

  useEffect(() => {
    setShowFilters(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const amt = size([start_date, end_date].filter((f) => !!f));
    if (amt == 1 && !state.useExact) {
      setState((s) => ({ ...s, useExact: true }));
    } else if (amt == 0 && state.useExact) {
      setState((s) => ({ ...s, useExact: false }));
      handleDatesChange({ target: { value: 'all' } });
    }
  }, [filter]);

  const changeFilter = (filt) => {
    if (useInternalFilter) {
      const val = { ...filt };
      setIntFilter(val);
      props.onChange && props.onChange(val);
    } else {
      dispatch(setFilter('reports', display, filt));
    }
  };

  function handleStartDateChange(date) {
    const filt = { ...filter };
    set(filt, `start_date`, date);
    set(filt, 'daterange', 'all');
    changeFilter(filt);
  }

  function handleEndDateChange(date) {
    const filt = { ...filter };
    set(filt, `end_date`, date);
    set(filt, 'daterange', 'all');
    changeFilter(filt);
  }

  function handleDatesChange(e) {
    const val = e.target.value;
    const res = DateRanges.find((d) => d.value == val);
    const filt = { ...filter };
    get(res, 'vals', []).map((v, i) => {
      set(filt, i === 0 ? 'start_date' : 'end_date', v);
    });
    set(filt, 'daterange', val);
    changeFilter(filt);
  }

  function handleScorecardChange(e) {
    changeFilter(set({ ...filter }, `scorecard_id`, e.target.value));
  }

  function handleEditionChange(e) {
    changeFilter(set({ ...filter }, `edition_id`, e.target.value));
  }

  function handleSystemChange(e) {
    changeFilter(set({ ...filter }, `superset_id`, e.target.value));
  }

  function handleAuthorChange(e) {
    changeFilter(set({ ...filter }, `user_id`, e.target.value));
  }

  function handleQualityChange(e) {
    changeFilter(set({ ...filter }, `quality_type`, e.target.value));
  }

  const handleRealmType = (e, val) => {
    changeFilter(set({ ...filter }, `realm_type`, xor(realm_type, [val])));
  };

  const handleVendorChange = (e) => {
    const vend = get(vendors, 'data', []).find((v) => v.id == e.target.value);
    changeFilter(set({ ...filter }, `${prefixToUse}_id`, get(vend, `${prefixToUse}Id`, null)));
  };

  const handleStatusChange = (e) => {
    changeFilter(set({ ...filter }, 'status', e.target.value));
  };

  function toggleFilters() {
    setShowFilters(!showFilters);
  }

  const canShowFilter = (f) => {
    if (props.onlyFilters) {
      return props.onlyFilters.includes(f);
    } else {
      return true;
    }
  };

  const hasVendors = size(get(vendors, 'data')) > 0;
  const client = get(vendors, 'data', []).find((v) => get(v, [prefixToUse, 'id']) == get(filter, `${prefixToUse}_id`));

  return (
    <Box displayPrint={'none'}>
      {isMobile && (
        <div className={clsx(classes.filterRow, classes.filterRowMobile)}>
          <Button className="FilterBtn" aria-label="filters" size="small" onClick={toggleFilters}>
            <FilterIcon fontSize="inherit" className={classes.icon} /> {'Filters'}
          </Button>
          <FilterChips id={`reports.${display}`} />
        </div>
      )}

      <DiagContainer useDialog={isMobile} onClose={toggleFilters} open={showFilters}>
        <Typography variant="overline">Filters</Typography>

        <form autoComplete="off" margin="normal" className={clsx(showFilters ? '' : classes.hidden, isMobile ? classes.mobileFormContainer : '')}>
          <Grid container>
            <div style={props.asRow ? { display: 'flex', flexDirection: 'row' } : { width: '100%' }}>
              {display === 'data' && canShowFilter('edition') && (
                <Grid item xs={12} className={classes.formGrid}>
                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                      Edition
                    </FormLabel>
                    <Select value={edition_id} onChange={handleEditionChange} input={<Input name="scorecard" id="Standard-scorecard-simple" />}>
                      <MenuItem value={0}>
                        <em>Any</em>
                      </MenuItem>
                      {size(editions.data) &&
                        editions.data.map((card, i) => {
                          return (
                            <MenuItem key={`${card.id}-${i}`} value={card.id}>
                              {card.name}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {canShowFilter('scorecard') && (
                <Grid item xs={12} className={classes.formGrid}>
                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                      Scorecard
                    </FormLabel>
                    <Select value={scorecard_id} onChange={handleScorecardChange} input={<Input name="scorecard" id="Standard-scorecard-simple" />}>
                      <MenuItem value={0}>
                        <em>Any</em>
                      </MenuItem>
                      {size(scorecards.data) &&
                        scorecards.data.map((card, i) => {
                          return (
                            <MenuItem key={`${card.id}-${i}`} value={card.id}>
                              {card.name}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {canShowFilter('location') && (
                <Grid item xs={12} className={classes.formGrid}>
                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel className={classes.formLabel}>{'Location Type'}</FormLabel>
                    <FormGroup>
                      {RealmTypes.map((rt, i) => {
                        const ICO = ICONS[rt.icon];
                        return (
                          <FormControlLabel
                            key={i}
                            control={<Checkbox checked={isArray(realm_type) && realm_type.includes(rt.value)} onChange={(e) => handleRealmType(e, rt.value)} value={rt.value} />}
                            label={
                              <div className={classes.checkRow}>
                                <ICO className={classes.checkIcon} />
                                {rt.title}
                              </div>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>
                </Grid>
              )}

              {canShowFilter('startDate') && (
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <div>
                    <Grid item xs={12} className={classes.formGrid}>
                      <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                        <Row spaceBetween className={classes.dateRangeRowPadding}>
                          <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                            Date
                          </FormLabel>
                          <Button variant="text" className={classes.linkText} onClick={() => setState((s) => ({ ...s, useExact: !s.useExact }))}>
                            {state.useExact ? 'Use Time Frame' : 'Use Date Range'}
                          </Button>
                        </Row>
                        {!state.useExact ? (
                          <Select value={daterange} onChange={handleDatesChange} className={classes.tinyMarginTop}>
                            {DateRanges.map((card, i) => {
                              return (
                                <MenuItem key={`${card.value}-${i}`} value={card.value}>
                                  {card.title}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        ) : (
                          <div style={props.asRow ? { flexDirection: 'row', display: 'flex', minWidth: 400 } : {}}>
                            <KeyboardDatePicker
                              id="mui-pickers-date_start"
                              label="Start Date"
                              format="MM/dd/yyyy"
                              autoOk={true}
                              value={start_date}
                              onChange={handleStartDateChange}
                              className={clsx(classes.datePickerPadding)}
                              KeyboardButtonProps={{
                                'aria-label': 'start date',
                              }}
                            />
                            <KeyboardDatePicker
                              id="mui-pickers-date_end"
                              label="End Date"
                              format="MM/dd/yyyy"
                              autoOk={true}
                              value={end_date}
                              onChange={handleEndDateChange}
                              className={clsx(classes.datePickerPadding)}
                              KeyboardButtonProps={{
                                'aria-label': 'end date',
                              }}
                            />
                          </div>
                        )}
                      </FormControl>
                    </Grid>
                  </div>
                </MuiPickersUtilsProvider>
              )}

              {hasVendors && canShowFilter('vendors') && (
                <Grid item xs={12} className={classes.formGrid}>
                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                      Company
                    </FormLabel>
                    <Select value={get(client, 'id', 0)} onChange={handleVendorChange}>
                      <MenuItem value={0}>
                        <em>Any</em>
                      </MenuItem>
                      {get(vendors, 'data', [])
                        .sort((a, b) => sortAlphaNum(a, b, 'vendor.name'))
                        .map((vend) => {
                          return (
                            <MenuItem key={`${vend.id}`} value={vend.id}>
                              {get(vend, [prefixToUse, 'name'], '')}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {display === 'data' && canShowFilter('status') && (
                <Grid item xs={12} className={classes.formGrid}>
                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                      Status
                    </FormLabel>
                    <Select value={status || 'active'} onChange={handleStatusChange}>
                      {ReportDataStatus.map((vend) => {
                        return (
                          <MenuItem key={`${vend.value}`} value={vend.value}>
                            {vend.title}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {status === 'review' && (
                    <Text type="label_small" italic>
                      Please note that “Review” data only displays for assessments that are “Final”, for access reasons.
                    </Text>
                  )}
                </Grid>
              )}

              {display === 'photo' && (
                <Grid item xs={12} className={classes.formGrid}>
                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                      System
                    </FormLabel>
                    <Select value={superset_id || 0} onChange={handleSystemChange}>
                      <MenuItem value={0}>
                        <em>Any</em>
                      </MenuItem>
                      {!!size(supersets.data) &&
                        (supersets.data || []).map((card, i) => {
                          return (
                            <MenuItem key={`${card.id}-${i}`} value={card.id}>
                              {card.name}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>

                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                      Author
                    </FormLabel>
                    <Select value={user_id || 0} onChange={handleAuthorChange}>
                      <MenuItem value={0}>
                        <em>Any</em>
                      </MenuItem>
                      {!!size(users.data) &&
                        users.data.map((user, i) => {
                          return (
                            <MenuItem key={`${user.id}-${i}`} value={user.id}>
                              {user.name}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>

                  <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                    <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                      Quality
                    </FormLabel>
                    <Select value={quality_type} onChange={handleQualityChange}>
                      {QualityType.map((qt) => (
                        <MenuItem value={qt.value}>{qt.value === 1 ? <em>{qt.title}</em> : qt.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </div>
          </Grid>
        </form>
      </DiagContainer>
    </Box>
  );
}
